import ManageExtraActivity from 'common/activeElements/modals/ManageExtraActivity';
import ModalAbsence from 'common/activeElements/modals/ModalAbsence';
import ModalAddEmploymentType from 'common/activeElements/modals/ModalAddEmploymentType';
import ModalDeleteAbsence from 'common/activeElements/modals/ModalDeleteAbsence';
import ModalDeleteActivity from 'common/activeElements/modals/ModalDeleteActivity';
import ModalDeleteExtraActivity from 'common/activeElements/modals/ModalDeleteExtraActivity';
import ModalManualReconciliation from 'common/activeElements/modals/ModalManualReconciliation';
import { MODAL_NAMES } from 'common/activeElements/modals/modalNames';
import ModalSetHomeLocation from 'common/activeElements/modals/ModalSetHomeLocation';
import ModalSetOnHoldDown from 'common/activeElements/modals/ModalSetOnHoldDown';
import ModalSetOnProtection from 'common/activeElements/modals/ModalSetOnProtection';
import ModalTravelToLocation from 'common/activeElements/modals/ModalTravelToLocation';
import ModalUpdateCrewLocation from 'common/activeElements/modals/ModalUpdateCrewLocation';
import EmploymentLog from 'common/activeElements/sidebars/EmploymenLog';
import LocationsLog from 'common/activeElements/sidebars/LocationLog';
import { SIDEBAR_NAMES } from 'common/activeElements/sidebars/sidebarsNames';
import WorkAssignmentDetails from 'common/activeElements/sidebars/WorkAssignmentDetails';
import EditInfo from 'pages/CrewMemberDetails/components/EditInfo';
import personalTimelineStore from 'pages/CrewMemberDetails/components/PersonalTimeline/PersonalTimelineStore';

/**
 * String key used by the ActiveElements system to scope instances for this page.
 * @type {string}
 */

export const PAGE_KEY = 'CrewMemberDetails';

/**
 * Active elements (modals + sidebars) configuration for the Crew Member Details page.
 *
 * Сomponents here rely on unified, already-resolved context for:
 * - employeeId (Crew Member ID)
 * - baseTimezone (IANA timezone string for date/time handling)
 * - currentEmploymentTypeId (current employment type for logs)
 * - currentHomeBase (current home base object; used by location log)
 * Centralized factory bind context to correct elements and disperses callbacks 
 to update action log and personal timeline
 *
 * @module CrewMemberDetailsActiveElements
 */

/**
 * ISO 8601 timestamp string, e.g. "2025-10-29T09:30:00Z"
 * @typedef {string} IsoTimestamp
 */

/**
 * Current home base descriptor.
 * @typedef {Object} HomeBase
 * @property {string|number} id - Home base identifier.
 * @property {IsoTimestamp} effectiveTimestamp - When this home base became effective.
 */

/**
 * Props injected by the ActiveElements Host at render time.
 * (Do not supply these manually when building the config; they are forwarded by the Host.)
 * @typedef {Object} HostInjectedProps
 * @property {() => void} onClose - Closes the active element (modal/sidebar).
 * @property {any} [openPayload] - Optional payload passed when opening the element.
 */

/**
 * React component type used for active elements.
 * Receives Host-injected props plus any component-specific props provided below.
 * @callback ActiveElementComponent
 * @param {HostInjectedProps & Object<string, any>} props
 * @returns {import('react').ReactElement}
 */

/**
 * Config entry describing a single active element (modal or sidebar).
 * @typedef {Object} ActiveElementConfig
 * @property {string} name - Unique name matching MODAL_NAMES/SIDEBAR_NAMES.
 * @property {ActiveElementComponent} Component - Factory component; page-level props are bound here.
 */

/**
 * Builds the Active Elements configuration array for the Crew Member Details page.
 *
 * All arguments are required and must be pre-resolved for the selected crew member.
 *
 * @param {Object} params
 * @param {string|number} params.employeeId - The crew member (employee) identifier used by all modals/sidebars.
 * @param {string} params.baseTimezone - IANA timezone, e.g. "Europe/Warsaw".
 * @param {string|number} params.currentEmploymentTypeId - Current employment type id used by EmploymentLog.
 * @param {HomeBase} params.currentHomeBase - Current home base ({ id, effectiveTimestamp }) used by LocationsLog.
 * @returns {ActiveElementConfig[]} Ordered list of modal and sidebar configs consumed by the ActiveElements Host.
 *
 * @example
 * makeActiveElementsConfig({
 *   employeeId: '12345',
 *   baseTimezone: 'Europe/Warsaw',
 *   currentEmploymentTypeId: 'B2B',
 *   currentHomeBase: { id: 'WAW', effectiveTimestamp: '2025-10-01T00:00:00Z' },
 * });
 */

export const makeActiveElementsConfig = ({
	employeeId,
	baseTimezone,
	currentEmploymentTypeId,
	currentHomeBase,
	refetchTimelineByDate,
	refetchActionsLog,
	refetchCrewMemberInfo,
}) => {
	const refetchTimelineAndLog = (date) => {
		refetchTimelineByDate(date);
		refetchActionsLog();
	};

	const refetchCrewMemberInfoTLAndLogs = () => {
		refetchActionsLog();
		// Re-fetch crew member info when updating home base location and employment type
		refetchCrewMemberInfo();
	};

	const patchExtraActivityAndRefetchLog = async (sequence) => {
		await personalTimelineStore.mergeExtraActivitySequence(sequence);
		refetchActionsLog();
	};

	const deleteExtraActivity = async (workAssignmentKey) => {
		const success = await personalTimelineStore.rejectExtraActivityAssignment(workAssignmentKey);

		if (success) {
			refetchActionsLog();
		}

		return success;
	};

	return [
		{
			name: MODAL_NAMES.absence,
			Component: (props) => (
				<ModalAbsence
					employeeId={employeeId}
					baseTimezone={baseTimezone}
					onActionSuccess={refetchTimelineAndLog}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.deleteAbsence,
			Component: (props) => (
				<ModalDeleteAbsence onActionSuccess={refetchTimelineAndLog} {...props} />
			),
		},
		{
			name: MODAL_NAMES.traveling,
			Component: (props) => (
				<ModalTravelToLocation
					onActionSuccess={refetchTimelineAndLog}
					employeeId={employeeId}
					baseTimezone={baseTimezone}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.protection,
			Component: (props) => (
				<ModalSetOnProtection
					onActionSuccess={refetchTimelineAndLog}
					employeeId={employeeId}
					baseTimezone={baseTimezone}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.holdDown,
			Component: (props) => (
				<ModalSetOnHoldDown employeeId={employeeId} baseTimezone={baseTimezone} {...props} />
			),
		},
		{
			name: MODAL_NAMES.employmentType,
			Component: (props) => (
				<ModalAddEmploymentType
					employeeId={employeeId}
					baseTimezone={baseTimezone}
					onActionSuccess={refetchCrewMemberInfoTLAndLogs}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.setHomeLocation,
			Component: (props) => (
				<ModalSetHomeLocation
					employeeId={employeeId}
					onActionSuccess={refetchCrewMemberInfoTLAndLogs}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.updateCrewLocation,
			Component: (props) => (
				<ModalUpdateCrewLocation
					employeeId={employeeId}
					onActionSuccess={refetchActionsLog}
					{...props}
				/>
			),
		},
		{
			name: SIDEBAR_NAMES.waDetails,
			Component: (props) => <WorkAssignmentDetails crewMemberId={employeeId} {...props} />,
		},
		{
			name: SIDEBAR_NAMES.employmentLog,
			Component: (props) => (
				<EmploymentLog
					employeeId={employeeId}
					currentEmploymentTypeId={currentEmploymentTypeId}
					{...props}
				/>
			),
		},
		{
			name: SIDEBAR_NAMES.locationsLog,
			Component: (props) => (
				<LocationsLog employeeId={employeeId} currentHomeBase={currentHomeBase} {...props} />
			),
		},
		{
			name: MODAL_NAMES.manualReconciliation,
			Component: (props) => (
				<ModalManualReconciliation
					baseTimezone={baseTimezone}
					onActionSuccess={refetchTimelineAndLog}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.updateAuxiliaryWAStatus,
			Component: (props) => (
				<ModalDeleteActivity
					employeeId={employeeId}
					baseTimezone={baseTimezone}
					onActionSuccess={refetchTimelineAndLog}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.manageExtraActivity,
			Component: (props) => (
				<ManageExtraActivity
					baseTimezone={baseTimezone}
					onActionSuccess={patchExtraActivityAndRefetchLog}
					{...props}
				/>
			),
		},
		{
			name: MODAL_NAMES.deleteExtraActivity,
			Component: (props) => (
				<ModalDeleteExtraActivity
					isDeletingExtraActivity={() => personalTimelineStore.isDeletingExtraActivity}
					onDeleteExtraActivityAssignment={deleteExtraActivity}
					{...props}
				/>
			),
		},
		{
			name: SIDEBAR_NAMES.editInfo,
			Component: (props) => (
				<EditInfo
					isShown
					id={employeeId}
					driverInfo={props.initialData?.driverInfo}
					onClose={props.onClose}
					onSuccess={() => {
						refetchCrewMemberInfo();
						props.onClose();
					}}
					onError={() => {}}
				/>
			),
		},
	];
};
